//@ts-nocheck
/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { ChangeEvent, Component } from 'react';
import { Layout, Modal } from 'antd';
import { withRouter, RouteComponentProps, Route } from 'react-router';
import moment from 'moment';
import { Header } from './header';
import { SideMenu } from './side-menu';
import { DashRoute } from '../_shared/interfaces/dashroute';
import styles from './dashboard.styles';
import {
  filterBasedOnRoles,
  userHasRole,
  isPB,
  isServiceOrgUser,
  isNativeAppBrowser,
  sendNativeEvent,
} from '../utils';

import { AppState } from '../app.state';
import { Routes, SideRoutes, AdminSideRoutes } from './dashboard.routes';
import Pusher from 'pusher-js';
import CONFIG, { getEnv } from '../config';
import { Publish } from '../_shared/interfaces/publish';
import { Footer } from '../footer';
import { Search } from './header/search.component';
import { Button } from '../_shared/button';
import { REG_KEY } from '../registration';
import { uniqBy, get } from 'lodash';
import { Equipment } from '../_shared/interfaces/equipment';
import { Profile } from '../_shared/interfaces/profile';
import { User } from '../_shared/interfaces/user';

const config = CONFIG();

interface IProps extends RouteComponentProps {
  logout: () => void;
  retrieveData: () => Promise<void>;
  sState: AppState;
  addToMap: (pubs: Publish[]) => Publish[];
  updateEquipmentPusher: (equipment: Equipment) => void;
  updateUserPusher: (user: User) => void;
}

interface IState {
  headerRoutes: DashRoute[];
  sideRoutes: DashRoute[];
  ellipsisRoutes: DashRoute[];
  allRoutes: { [key: string]: DashRoute };
  searchValue?: string | undefined;
  profiles?: Profile[];
  externalModal?: { url: string; title: string };
}

export class DashboardComponent extends Component<IProps, IState> {
  pusher: Pusher;
  constructor(props: IProps) {
    super(props);
    this.props.retrieveData();
    const { sState } = props;
    const isOrgAdmin = userHasRole([2], props.sState);
    const isOrgGroupAdmin = userHasRole([3], props.sState);
    const isProductionRole = userHasRole([5], props.sState);
    const profiles: Profile[] = get(sState, 'dash.profiles');
    const isNative = isNativeAppBrowser();

    // const userHasHandPiece: boolean = models.includes('handpiece');

    const getAdminHeaderRoute = () => {
      let route = [Routes.admin];
      if (isOrgAdmin) {
        route = [
          {
            ...Routes.admin,
            path: '/dashboard/myOrg',
          },
        ];
      } else if (isOrgGroupAdmin) {
        route = [
          {
            ...Routes.admin,
            path: '/dashboard/manageGroups',
          },
        ];
      }
      return route;
    };

    let state;
    if (isProductionRole) {
      state = {
        allRoutes: Routes,
        ellipsisRoutes: [
          Routes.account,
          Routes.changePassword,
          {
            title: 'Sign Out',
            icon: { type: 'logout' },
            path: '/dashboard/logout',
            onClick: props.logout,
          },
        ],
        headerRoutes: [Routes.manufactureProcess],
        sideRoutes: [Routes.manufactureProcess],
      };
      props.history.push('/dashboard/manufactureProcess');
    } else {
      // AER-1585: the kebab is rendered on desktop too (next to the Dashboard
      // tab in the header), so only surface a 'Dashboard' entry when we're on
      // a narrow/mobile viewport or inside the native app webview. On desktop
      // the header tab already provides home nav.
      const isPhoneBreak = isPB(sState);
      const showDashboardInKebab = isPhoneBreak || isNative;
      const nativeEllipsisRoutes: DashRoute[] = [
        ...(showDashboardInKebab ? [Routes.landing] : []),
        Routes.account,
        Routes.notificationSettings,
        Routes.changePassword,
        Routes.feedback,
        Routes.scheduling,
        {
          title: 'Register Equipment',
          icon: { type: 'wifi' },
          path: '',
          onClick: () => this.nativePress(),
        },
        {
          title: 'Terms of Use',
          icon: { type: 'file-text' },
          path: '',
          onClick: () => this.openExternal('https://www.aerasintel.com/terms-of-use', 'Terms of Use'),
        },
        {
          title: 'Privacy Policy',
          icon: { type: 'safety-certificate' },
          path: '',
          onClick: () => this.openExternal('https://www.aerasintel.com/privacy-policy', 'Privacy Policy'),
        },
        {
          title: 'Sign Out',
          icon: { type: 'logout' },
          path: '/dashboard/logout',
          onClick: props.logout,
        },
      ];
      state = {
        allRoutes: Routes,
        ellipsisRoutes: isNative
          ? nativeEllipsisRoutes
          : ([
              ...(showDashboardInKebab ? [Routes.landing] : []),
              Routes.account,
              Routes.manageUsers,
              Routes.manageGroups,
              Routes.notificationSettings,
              Routes.changePassword,
              {
                title: 'Resources',
                icon: { type: 'resources', lib: 'SvgIcon' },
                onClick: this.openResources,
                path: '/dashboard/resources',
                // allowedRoles: [1, 2, 3, 4, 5, 6],
              },
              {
                title: 'Sign Out',
                icon: { type: 'logout' },
                path: '/dashboard/logout',
                onClick: props.logout,
              },
            ].filter(f => !!f) as DashRoute[]),
        headerRoutes: [
          Routes.landing,
          Routes.scheduling,
          Routes.profiles,
          ...getAdminHeaderRoute(),
          Routes.reporting,
        ],
        sideRoutes: SideRoutes,
        searchValue: undefined,
        profiles,
      };
    }

    this.state = state;

    this.pusher = new Pusher(config.pusher.key, {
      cluster: config.pusher.cluster,
      forceTLS: true,
    });

    const channel = this.pusher.subscribe(config.pusherChannelName);

    channel.bind(`equipmentCreate`, (data: any) => {
      //@ts-ignore
      // console.devWarn('equipmentCreate:', data);
      const { sState } = this.props;
      const equip = data as Equipment;
      const cUser: any = get(sState, 'auth.user');

      if (!equip.modelId) {
        return;
      }

      const isOrgUser = userHasRole([2, 3, 4], sState);
      const isProdUser = userHasRole([5], sState);
      if (isOrgUser) {
        if (
          (!!equip.dentalOrgId && equip.dentalOrgId === cUser.orgId) ||
          (!!equip.serviceOrgId && equip.serviceOrgId === cUser.orgId)
        ) {
          props.updateEquipmentPusher(equip);
        }
        return;
      }
      if (isProdUser) {
        // AER-1725: this used to assign the freshness counter onto `equip` before
        // handing it to updateEquipmentPusher, so it became part of the redux
        // equipment object and then part of the record on the next save from any
        // screen — 1,193 equipment records carry `_testDiff` because of it. It is a
        // local filter test and nothing downstream reads it; keep it off the record.
        const testDiff = equip.test
          ? moment().diff(moment(equip.test), 'days')
          : undefined;

        if (testDiff !== undefined && testDiff <= 7) {
          props.updateEquipmentPusher(equip);
        }
        return;
      }
      props.updateEquipmentPusher(equip);
    });
    channel.bind(`userCreate`, (data: any) => {
      const { sState } = this.props;
      const user = data as User;
      const cUser: any = get(sState, 'auth.user');
      const isOrgUser = userHasRole([2, 3, 4], sState);
      const isProdUser = userHasRole([5], sState);
      if (isProdUser) return;
      if (isOrgUser) {
        if (user.orgId === cUser.orgId) {
          props.updateUserPusher(user);
        }
        return;
      }
      props.updateUserPusher(user);
    });
    channel.bind('test', (data: any) => {
      console.warn(data);
    });
  }

  componentDidUpdate(prevProps: IProps) {
    const prevSearchString = get(
      prevProps,
      'sState.dash.equipmentFilters.search'
    );
    const currentSearchString = get(
      this.props,
      'sState.dash.equipmentFilters.search'
    );
    if (prevSearchString !== currentSearchString && !currentSearchString) {
      this.setState({ searchValue: undefined });
    }
    if (
      get(prevProps.sState, 'dash.profiles') !==
      get(this.props.sState, 'dash.profiles')
    ) {
      this.setState({
        profiles: get(this.props.sState, 'dash.profiles'),
      });
    }
    // AER-1591: reset the content scroll position when the route changes.
    // The `Layout.Content` element is the scroll viewport on mobile, and
    // it retains scrollTop across navigation — landing the user mid-page
    // when entering Show Alert History / Edit Equipment Details. Snap to
    // the top on every path change to match user expectations.
    if (
      get(prevProps, 'history.location.pathname') !==
      get(this.props, 'history.location.pathname')
    ) {
      const el = document.getElementById('ContentContainer');
      if (el) {
        el.scrollTop = 0;
      }
    }
  }

  nativePress = () => {
    sendNativeEvent({ action: 'router', data: '/setupwifi' });
  };

  openResources = () => {
    const { sState } = this.props;
    const isDZAdmin = userHasRole([0, 1, 7], sState);
    const isSOUser = isServiceOrgUser(sState);
    const url =
      isDZAdmin || isSOUser
        ? 'https://www.dentalez.com/aeras-reference-page-service-orgs-video-tutorials/'
        : 'https://www.dentalez.com/aeras-reference-page-dental-orgs-video-tutorials/';

    var win = window.open(url, '_blank');
    win && win.focus();
  };

  openExternal = (url: string, title: string = '') => {
    // AER-1594: in the native app webview, window.open opens the URL in the
    // same WebView with no way back. Show it in a closeable modal instead.
    if (isNativeAppBrowser()) {
      this.setState({ externalModal: { url, title } });
      return;
    }
    var win = window.open(url, '_blank');
    win && win.focus();
  };

  onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    this.setState({ searchValue: value });
  };

  changeRoute = (route: DashRoute) => {
    if (route.sideRoutes) {
      return this.setState({ sideRoutes: route.sideRoutes }, () =>
        this.props.history.push(route.path)
      );
    }
    this.props.history.push(route.path);
  };
  findRoute = () => {
    const { location } = this.props.history;
    let route;
    for (let key in Routes) {
      let r = Routes[key];
      if (r && r.path === location.pathname) {
        route = r;
        break;
      }
    }
    return route;
  };
  componentWillUnmount = () => {
    this.pusher.unsubscribe(config.pusherChannelName);
  };
  render() {
    const { sState } = this.props;
    const { headerRoutes, ellipsisRoutes, allRoutes } = this.state;
    const currentRoute = this.findRoute();
    const _headerRoutes = filterBasedOnRoles(headerRoutes, sState);
    const _sideRoutes = filterBasedOnRoles(
      currentRoute && currentRoute.sideRoutes,
      sState
    );
    let _ellipsisRoutes = filterBasedOnRoles(ellipsisRoutes, sState);
    const ispb = isPB(sState);
    const isNative = isNativeAppBrowser();
    if (ispb && !isNative) {
      const sroutes = filterBasedOnRoles(AdminSideRoutes, sState);

      // AER-1585: keep Dashboard pinned to the top of the merged kebab menu.
      _ellipsisRoutes = uniqBy(
        filterBasedOnRoles(
          [Routes.landing, Routes.scheduling, ...sroutes, ..._ellipsisRoutes],
          sState
        ),
        'path'
      );
    }

    const showSearch = get(currentRoute, 'showSearch', true);
    const showPBSearch = get(currentRoute, 'showPBSearch', true) && showSearch;

    return (
      <Layout css={css(styles.container)}>
        <Header
          routes={_headerRoutes}
          ellipsisRoutes={_ellipsisRoutes}
          changeRoute={this.changeRoute}
          showSearch={showSearch}
          searchValue={this.state.searchValue}
          onChange={this.onChange}
        />
        <Layout>
          {!ispb && _sideRoutes.length > 0 && (
            <SideMenu
              routes={_sideRoutes}
              currentRoute={currentRoute}
              changeRoute={this.changeRoute}
            />
          )}
          <Layout>
            <Layout.Content
              id={'ContentContainer'}
              css={css([
                styles.contentContainer,
                ispb && `margin-bottom: 40px;`,
              ])}
            >
              {ispb && showPBSearch && (
                <div css={css(styles.searchContainer, styles.pbStickyTop)}>
                  <Search
                    searchValue={this.state.searchValue}
                    onChange={this.onChange}
                  />
                  {this.props.history.location.pathname === '/dashboard' &&
                    !userHasRole([5, 6, 7], sState) && (
                      <Button
                        css={css('margin-left: 8px; flex-shrink: 0;')}
                        onClick={() => {
                          localStorage.removeItem(REG_KEY);
                          // AER-1589: match the unauthenticated and menu-accessed
                          // "Register Equipment" buttons — in the native app webview
                          // route to the camera QR scanner first instead of jumping
                          // straight to the manual-entry form.
                          if (isNativeAppBrowser()) {
                            this.nativePress();
                            return;
                          }
                          this.props.history.push('/eqRegistration');
                        }}
                        title={'Register Equipment'}
                        icon={'plus'}
                      />
                    )}
                </div>
              )}
              {Object.keys(allRoutes).map((key, i) => (
                <Route key={i} {...allRoutes[key]} />
              ))}
              {ispb &&
                this.props.history.location.pathname !== '/dashboard' && (
                  <Footer shouldExtend={true} />
                )}
            </Layout.Content>
          </Layout>
        </Layout>
        <Modal
          open={!!this.state.externalModal}
          title={this.state.externalModal && this.state.externalModal.title}
          onCancel={() => this.setState({ externalModal: undefined })}
          footer={null}
          width="100%"
          style={{ top: 10, maxWidth: 'calc(100vw - 20px)', paddingBottom: 0 }}
          styles={{ body: { padding: 0, height: 'calc(100vh - 80px)' } }}
          destroyOnHidden
        >
          {this.state.externalModal && (
            <iframe
              src={this.state.externalModal.url}
              title={this.state.externalModal.title}
              style={{ border: 0, width: '100%', height: '100%' }}
            />
          )}
        </Modal>
      </Layout>
    );
  }
}

export const Dashboard = withRouter(DashboardComponent);
